import { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo, useSyncExternalStore } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { navSections, type NavMainItem, type NavSection, type NavSubItem } from './navConfig'
import { useEffectivePermissions } from '../auth/useEffectivePermissions'
import { modules } from '../modules/registry'
import { useThemeStore } from '../store/themeStore'
import {
  HOVER_OPEN_MS, RAIL_W, PANEL_W, within, navGeo, railIcons, evaluate, fireAction,
  blockedAfterClose, blockedAfterPress, type Pt, type Geo, type RailIcon,
} from './sidebarHover'

// Brand block per theme. Mammas keeps the teal identity; St. Pauli uses
// a brown tile with "SP" monogram and a red/brown stripe, echoing the
// club's kit without using any trademarked marks.
const BRANDS = {
  mammas:  { monogram: 'MC', name: 'Mammas Canteen', tagline: 'ERP v2.0',          stripe: false },
  stpauli: { monogram: 'SP', name: 'FC St. Pauli',   tagline: 'ERP · Kiez Edition', stripe: true  },
} as const

// Idle-close time (owner answer, 19.10.2026). The hover rule itself lives in sidebarHover.ts.
const IDLE_CLOSE_MS = 10_000
const BACKDROP_FADE_MS = 250
const SLIDE_MS = 300
const reducedMotion = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

// ── Desktop breakpoint (Tailwind lg) ─────────────────────────────────────────
const LG_QUERY = '(min-width: 1024px)'
function subscribeDesktop(cb: () => void) {
  const mq = window.matchMedia(LG_QUERY)
  mq.addEventListener('change', cb)
  return () => mq.removeEventListener('change', cb)
}
function useIsDesktop() {
  return useSyncExternalStore(subscribeDesktop, () => window.matchMedia(LG_QUERY).matches, () => true)
}

// ── Module access helper ─────────────────────────────────────────────────────
// Resolves visibility through ops.RoleModuleAccess (DB) instead of registry's
// hardcoded roles arrays. canRead is supplied by useEffectivePermissions.
// Paths not registered in modules[] (e.g. external/non-app links) default to
// allowed — same as the legacy behaviour. A nav entry that names its module
// (several pages of one module, e.g. the three QM setup pages) uses that module.

function hasAccessToPath(path: string, canRead: (moduleId: string) => boolean, moduleId?: string) {
  if (moduleId) return canRead(moduleId)
  const module = modules.find(m => m.path === path)
  if (!module) return true
  return canRead(module.id)
}

// ── Active matching ──────────────────────────────────────────────────────────
// Exactly ONE visible nav path is active: the longest one that is the pathname or a
// parent of it. Deep pages (…/auswertung/schule/123) light up their nearest
// nav entry; a parent never lights up together with its sub-entry. Only the entries the
// user can see count (an IT.Admin-only Lab entry must not take the match from Imports).
function activeNavPath(pathname: string, sections: NavSection[]): string | null {
  let best: string | null = null
  for (const sec of sections) for (const it of sec.items) for (const p of [it.path, ...(it.subItems ?? []).map((si) => si.path)]) {
    const hit = p === '/' ? pathname === '/' : pathname === p || pathname.startsWith(p + '/')
    if (hit && (best == null || p.length > best.length)) best = p
  }
  return best
}
function sectionOfPath(path: string | null, sections: NavSection[]): string | null {
  if (path == null) return null
  const sec = sections.find((s) => s.items.some((it) => it.path === path || it.subItems?.some((si) => si.path === path)))
  return sec?.label ?? null
}

// ── Nav helpers ───────────────────────────────────────────────────────────────

const FOCUS_RING = 'focus-visible:ring-2 focus-visible:ring-primary/60'

// Accordion header — every section is collapsible; the section of the active
// route is open, the others closed.
function SectionLabel({ label, isOpen, onToggle, bodyId }: {
  label: string; isOpen: boolean; onToggle: () => void; bodyId: string
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      aria-controls={bodyId}
      className={`flex items-center w-full text-[10px] font-bold tracking-[0.08em] uppercase text-on-surface-variant hover:text-on-surface mt-[18px] mb-1.5 lg:mt-3 lg:mb-1 px-3 overflow-hidden whitespace-nowrap cursor-pointer outline-none rounded-[6px] ${FOCUS_RING}`}
    >
      <span className="flex-1 text-left truncate">{label}</span>
      <span
        aria-hidden="true"
        className={`material-symbols-outlined !text-[14px] transition-transform duration-200 motion-reduce:transition-none ${isOpen ? 'rotate-180' : 'rotate-0'}`}
      >
        expand_more
      </span>
    </button>
  )
}

function mainItemClass(isActive: boolean) {
  return [
    'relative flex items-center rounded-[8px] text-[13px] font-medium cursor-pointer transition-all duration-200 motion-reduce:transition-none mb-0.5 outline-none',
    FOCUS_RING,
    'gap-[10px] w-full px-3 py-[8px] lg:py-1.5',
    isActive
      ? 'bg-primary/10 text-primary font-semibold [&_.icon]:text-primary'
      : 'text-on-surface-variant hover:bg-surface-container hover:text-on-surface [&_.icon]:text-outline-variant',
  ].join(' ')
}

function subItemClass(isActive: boolean) {
  return [
    'flex items-center py-[7px] lg:py-1 gap-[10px] pl-[38px] pr-3 rounded-[6px] text-[12px] font-medium cursor-pointer transition-all duration-200 motion-reduce:transition-none mb-0.5 outline-none',
    FOCUS_RING,
    isActive
      ? 'bg-surface-container text-primary font-semibold [&_.icon]:text-primary'
      : 'text-outline hover:bg-surface-container hover:text-on-surface [&_.icon]:text-outline-variant',
  ].join(' ')
}

const sectionBodyId = (label: string) => `nav-sec-${label.replace(/[^a-zA-Z0-9]+/g, '-')}`

type VisibleItem = NavMainItem & { linkPath: string; subItems?: NavSubItem[] }

function MainNavItem({ item, isActive }: { item: VisibleItem; isActive: boolean }) {
  const shown = isActive
  return (
    <Link
      to={item.linkPath}
      // The Dashboard link must open the dashboard even for users with a saved landing module.
      state={item.path === '/' ? { fromNav: true } : undefined}
      viewTransition
      aria-current={isActive ? 'page' : undefined}
      className={mainItemClass(shown)}
    >
      {/* Active indicator pill (left edge) */}
      {shown && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-primary rounded-r-full" />
      )}
      <span aria-hidden="true" className="material-symbols-outlined icon flex-shrink-0 !text-[20px] lg:!text-[18px]">{item.icon}</span>
      {/* Wraps instead of "…": the panel's section column is narrower beside the rail. */}
      <span className="flex-1 min-w-0 break-words">
        {item.label}
      </span>
    </Link>
  )
}

function SubNavItem({ item, isActive }: { item: NavSubItem; isActive: boolean }) {
  return (
    <Link
      to={item.path}
      viewTransition
      aria-current={isActive ? 'page' : undefined}
      className={subItemClass(isActive)}
    >
      <span aria-hidden="true" className="material-symbols-outlined icon flex-shrink-0 !text-[16px] lg:!text-[14px]">{item.icon}</span>
      <span className="flex-1 min-w-0 break-words">{item.label}</span>
    </Link>
  )
}

function NavSkeleton() {
  return (
    <div className="px-3 pt-4 flex flex-col gap-3" role="status" aria-label="Menü wird geladen">
      {[72, 56, 64, 48, 68, 52].map((w, i) => (
        <div key={i} className="h-5 rounded-md bg-surface-container animate-pulse motion-reduce:animate-none" style={{ width: `${w}%` }} />
      ))}
    </div>
  )
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

export default function Sidebar() {
  const { canRead, isLoading: permsLoading, isError: permsError, raw: permsData, refetch: refetchPerms } = useEffectivePermissions()
  const themeId = useThemeStore((s) => s.themeId)
  const brand = BRANDS[themeId] ?? BRANDS.mammas
  const { pathname } = useLocation()
  const isDesktop = useIsDesktop()

  // Filter sections based on effective permissions (DB-driven, not registry).
  // A parent whose own page is denied but has an allowed sub links to the first allowed sub.
  // adminOnly entries (owner A19) need the effective role IT.Admin (server flag isAdmin).
  const filteredSections = useMemo(() => {
    const isItAdmin = permsData?.isAdmin === true
    return navSections.map(section => ({
      ...section,
      items: section.items.flatMap((item): VisibleItem[] => {
        if (item.adminOnly && !isItAdmin) return []
        const subItems = item.subItems?.filter(sub => hasAccessToPath(sub.path, canRead, sub.module))
        const hasMain = hasAccessToPath(item.path, canRead, item.module)
        if (!hasMain && !subItems?.length) return []
        return [{ ...item, subItems, linkPath: hasMain ? item.path : subItems![0].path }]
      }),
    })).filter(section => section.items.length > 0)
  },
  // canRead is rebuilt each render; the permission data it reads decides the result.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [permsData])

  // Desktop: the section rail opens as an OVERLAY (hover, a rail icon or the toggle);
  // the page never moves. Nothing is persisted.
  const [hoverOpen, setHoverOpen] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  const activePath = activeNavPath(pathname, filteredSections)
  const activeSection = sectionOfPath(activePath, filteredSections)
  // Accordion: one section open at a time, the one of the active route.
  const [openSection, setOpenSection] = useState<string | null>(activeSection)
  useEffect(() => {
    if (activeSection) setOpenSection(activeSection)
  }, [activeSection])

  const pointerInside = useRef(false)
  const focusInside = useRef(false)
  const hoverTimer = useRef<number | null>(null)
  const idleTimer = useRef<number | null>(null)
  // Position rule state (A21). geo = the fixed nav edges; the width is RAIL_W or PANEL_W.
  const geo = useRef<Geo>({ left: 16, top: 16, bottom: 784 })
  const icons = useRef<RailIcon[]>([])
  const armed = useRef<string | null>(null)  // target of the running hover timer
  const pos = useRef<Pt | null>(null)
  const hoverBlocked = useRef(false)      // after a close with the pointer on the rail
  const pressed = useRef<Pt | null>(null) // mouse button down: where it went down
  const pressMoved = useRef(false)
  const dragging = useRef(false)          // HTML5 drag in progress (R18)
  // Section to bring into view (and focus, by keyboard) once the panel has rendered.
  const reveal = useRef<{ label: string | null; focus: boolean } | null>(null)
  const hoverOpenRef = useRef(hoverOpen)
  hoverOpenRef.current = hoverOpen

  const overlayOpen = isDesktop ? hoverOpen : isMobileOpen
  const navReady = !permsLoading && !(permsError && !permsData)
  const expanded = isDesktop ? hoverOpen : true
  const iconMode = isDesktop && !expanded

  // Accordion falls back to the section of the active route whenever the overlay closes
  // (a rail icon may have opened another one).
  const activeSectionRef = useRef(activeSection)
  activeSectionRef.current = activeSection
  const openSectionRef = useRef(openSection)
  openSectionRef.current = openSection
  useEffect(() => {
    if (!overlayOpen && openSectionRef.current !== activeSectionRef.current) setOpenSection(activeSectionRef.current)
  }, [overlayOpen])

  // Backdrop stays mounted for the 0.25 s fade-out. The fade-in is a CSS keyframe on mount
  // (index.css), so every open path (hover, toggle, rail icon, hamburger) fades the same way.
  // It mounts in the same render as the open (no second render of the nav tree on open, A20).
  // Desktop: the backdrop goes when the panel's closing slide ends (`concealing`, below), in
  // the same render. Mobile: its own 0.25 s timer.
  const [backdropLeaving, setBackdropLeaving] = useState(false)
  const wasOpen = useRef(overlayOpen)
  useEffect(() => {
    const was = wasOpen.current
    wasOpen.current = overlayOpen
    // No state call on open (a no-op setState still costs a render). A reopen during the
    // fade cancels the timer below; `leaving` is then reset by the next close.
    if (overlayOpen) return
    // Reduced motion: no fade, so the backdrop goes at once.
    if (!was || isDesktop || reducedMotion()) return
    setBackdropLeaving(true)
    const t = window.setTimeout(() => setBackdropLeaving(false), BACKDROP_FADE_MS)
    return () => window.clearTimeout(t)
  }, [overlayOpen, isDesktop])

  // A20 — desktop slide without a layout per frame: the nav's layout width changes ONCE
  // (72 <-> 256 px) and the visible edge moves with an animated clip-path (index.css), on the
  // prototype's 0.3 s curve. While the closing slide runs (`concealing`) the nav keeps 256 px.
  const [concealing, setConcealing] = useState(false)
  useEffect(() => {
    if (!concealing) return
    const t = window.setTimeout(() => setConcealing(false), SLIDE_MS + 150)   // animationend fallback
    return () => window.clearTimeout(t)
  }, [concealing])
  const backdropMounted = overlayOpen || backdropLeaving || concealing

  // Root flag while the overlay / drawer is open: the floating Data-Bot button, panel and dock
  // are blurred, click-through and moved below the backdrop via CSS (index.css). The document
  // click listener below closes the sidebar for a click at their position.
  useEffect(() => {
    if (!overlayOpen) return
    document.documentElement.setAttribute('data-sidebar-overlay', '')
    return () => document.documentElement.removeAttribute('data-sidebar-overlay')
  }, [overlayOpen])

  const navRef = useRef<HTMLElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  // Closed mobile drawer: nothing focusable, nothing exposed. React 18 has no `inert` prop.
  const drawerClosed = !isDesktop && !isMobileOpen
  useEffect(() => {
    const el = navRef.current
    if (el) (el as HTMLElement & { inert: boolean }).inert = drawerClosed
  }, [drawerClosed])

  const cancelHover = useCallback(() => {
    if (hoverTimer.current != null) { window.clearTimeout(hoverTimer.current); hoverTimer.current = null }
    armed.current = null
  }, [])
  const cancelIdle = useCallback(() => {
    if (idleTimer.current != null) { window.clearTimeout(idleTimer.current); idleTimer.current = null }
  }, [])

  // Every close (menu entry, page click, Escape, toggle, idle) goes through here. With the
  // pointer on the rail, hover-open waits until a pointer position is outside the rail (A21).
  const closeOverlay = useCallback(() => {
    hoverBlocked.current = blockedAfterClose(geo.current, pos.current)
    cancelHover()
    if (hoverOpenRef.current && !reducedMotion()) setConcealing(true)
    setHoverOpen(false); setIsMobileOpen(false)
  }, [cancelHover])

  // Opens the panel, optionally with one section expanded and brought into view (A22).
  const openOverlayAt = useCallback((label: string | null, byKeyboard: boolean) => {
    if (label) setOpenSection(label)
    reveal.current = { label, focus: byKeyboard }
    setConcealing(false); setHoverOpen(true)
  }, [])

  // Toggle button: opens or closes the same overlay as hover, at once.
  const toggle = () => {
    cancelHover()
    if (hoverOpen) closeOverlay()
    else { setConcealing(false); setHoverOpen(true) }
  }

  // Listen for mobile toggle events
  useEffect(() => {
    const handleMobileToggle = () => setIsMobileOpen(prev => !prev)
    window.addEventListener('mobile-sidebar-toggle', handleMobileToggle)
    return () => window.removeEventListener('mobile-sidebar-toggle', handleMobileToggle)
  }, [])

  // Mobile drawer closes when the route changes (not on a click, so Ctrl+click keeps it).
  useEffect(() => { setIsMobileOpen(false) }, [pathname])

  // Escape closes the overlay / drawer.
  useEffect(() => {
    if (!overlayOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Focus inside the sidebar: move it to the toggle first, or it drops to <body>
      // when the panel goes back to the rail.
      if (navRef.current?.contains(document.activeElement)) {
        if (isDesktop) toggleRef.current?.focus()
        else document.querySelector<HTMLElement>('header button[aria-label="Menü öffnen"]')?.focus()
      }
      closeOverlay()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [overlayOpen, closeOverlay, isDesktop])

  // A15/A16/A17: a click anywhere outside the sidebar closes it AND still reaches the
  // clicked page element (the blurred page is click-through). Focus: the clicked element
  // keeps it, else it goes to the page content. The blurred bot button / chat window only
  // close the sidebar: their click is swallowed, so the bot stays as it was (A14/A17).
  useEffect(() => {
    if (!overlayOpen) return
    const inBot = (x: number, y: number) =>
      Array.from(document.querySelectorAll('#bot-launcher-button, [aria-labelledby="bot-panel-title"], [aria-labelledby="bot-dock-title"]')).some((el) => {
        const r = el.getBoundingClientRect()
        return x >= r.left && x <= r.right && y >= r.top && y <= r.bottom
      })
    // Nothing below the blurred bot elements may act: stop the whole press sequence there.
    // A touch tap there makes no click (preventDefault), so its touchend closes the sidebar.
    const swallow = (e: Event) => {
      const p = 'changedTouches' in e ? (e as TouchEvent).changedTouches[0] : (e as MouseEvent)
      if (!p || (e.target instanceof Node && navRef.current?.contains(e.target))) return
      if (!inBot(p.clientX, p.clientY)) return
      e.preventDefault(); e.stopImmediatePropagation()
      if (e.type === 'touchend') closeOverlay()
    }
    const onClick = (e: MouseEvent) => {
      const t = e.target
      if (!(t instanceof Element) || navRef.current?.contains(t)) return
      // The hamburger toggles the drawer itself; closing here would reopen it.
      if (t.closest('header button[aria-label^="Menü"]')) return
      closeOverlay()
      if (inBot(e.clientX, e.clientY)) { e.preventDefault(); e.stopImmediatePropagation(); return }
      window.setTimeout(() => {
        const a = document.activeElement
        if (!a || a === document.body || navRef.current?.contains(a)) document.getElementById('main-content')?.focus({ preventScroll: true })
      }, 0)
    }
    const swallowed = ['pointerdown', 'pointerup', 'mousedown', 'mouseup']
    const touches = ['touchstart', 'touchend']
    swallowed.forEach((t) => document.addEventListener(t, swallow, true))
    touches.forEach((t) => document.addEventListener(t, swallow, { capture: true, passive: false }))
    document.addEventListener('click', onClick, true)
    return () => {
      swallowed.forEach((t) => document.removeEventListener(t, swallow, true))
      touches.forEach((t) => document.removeEventListener(t, swallow, true))
      document.removeEventListener('click', onClick, true)
    }
  }, [overlayOpen, closeOverlay])

  // A15: a menu entry click closes the sidebar at once and the focus goes to the page.
  // Ctrl/Cmd/Shift/middle clicks open a new tab and leave the sidebar alone.
  const onNavClick = (e: React.MouseEvent) => {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
    if (!(e.target as Element).closest('a[href]')) return
    closeOverlay()
    // Focus would drop to <body> inside the now-inert/rail nav.
    document.getElementById('main-content')?.focus({ preventScroll: true })
  }

  // ── Hover-open + idle-close (A21) ─────────────────────────────────────────
  // The rail opens as an overlay after HOVER_OPEN_MS with the pointer on it — never while a
  // mouse button is down or a drag runs (R18), never right after a close until the pointer was
  // outside the rail. The overlay closes by itself after IDLE_CLOSE_MS, but the countdown only
  // runs while BOTH the pointer and the keyboard focus are outside the panel.
  const armIdle = useCallback(() => {
    cancelIdle()
    if (pointerInside.current || focusInside.current) return   // inside = in use, no countdown
    idleTimer.current = window.setTimeout(closeOverlay, IDLE_CLOSE_MS)
  }, [cancelIdle, closeOverlay])

  const ruleState = useCallback(() => ({
    open: hoverOpenRef.current, blocked: hoverBlocked.current, pressed: !!pressed.current,
    dragging: dragging.current, shown: openSectionRef.current,
  }), [])

  // A22: the section icon under the pointer AT FIRE TIME opens (closed) or is switched to (open).
  // Nothing happens when a higher layer (command palette, modal, drawer) covers the rail.
  const fireHover = useCallback(() => {
    hoverTimer.current = null; armed.current = null
    const p = pos.current
    const el = p ? document.elementFromPoint(p.x, p.y) : null
    const hit = {
      inNav: !!el && !!navRef.current?.contains(el),
      section: el?.closest<HTMLElement>('[data-rail-section]')?.dataset.railSection ?? null,
    }
    const act = fireAction(ruleState(), geo.current, icons.current, p, hit)
    if (act) openOverlayAt(act.section, false)
  }, [openOverlayAt, ruleState])

  // Re-evaluates the rule for the current pointer position. The timer restarts for each rail
  // icon the pointer moves onto (sidebarHover.evaluate).
  const update = useCallback(() => {
    const open = hoverOpenRef.current
    const r = evaluate({ ...ruleState(), armed: armed.current }, geo.current, icons.current, pos.current)
    hoverBlocked.current = r.blocked
    if (r.inside !== pointerInside.current) {
      pointerInside.current = r.inside
      if (open) { if (r.inside) cancelIdle(); else armIdle() }
    }
    if (r.timer === 'cancel') cancelHover()
    else if (r.timer === 'restart') {
      cancelHover()
      armed.current = r.target
      hoverTimer.current = window.setTimeout(fireHover, HOVER_OPEN_MS)
    }
  }, [armIdle, cancelHover, cancelIdle, fireHover, ruleState])

  // Rail rectangle + icon rows from layout values without CSS transforms (the mobile -> desktop
  // slide must not shift them). Measured on mount, resize, the nav's transitionend, a rail
  // scroll and whenever the rail content changes.
  const measure = useCallback(() => {
    const nav = navRef.current
    if (!nav) return
    const g = navGeo(nav)
    const top = nav.getBoundingClientRect().top
    geo.current = g
    icons.current = railIcons(g, top, Array.from(nav.querySelectorAll<HTMLElement>('[data-rail-section]'), (b) => {
      const r = b.getBoundingClientRect()
      return { section: b.dataset.railSection ?? '', top: r.top, bottom: r.bottom }
    }))
  }, [])

  // Document listeners for the rule (desktop only; touch has no hover).
  useEffect(() => {
    if (!isDesktop) return
    measure()
    const at = (e: MouseEvent) => { pos.current = { x: e.clientX, y: e.clientY } }
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      at(e)
      dragging.current = false          // no pointermove fires while an HTML5 drag runs
      if (e.buttons === 0) { pressed.current = null; pressMoved.current = false }
      else if (!pressed.current) { pressed.current = pos.current; pressMoved.current = true }   // pressed outside the page
      else if (Math.abs(e.clientX - pressed.current.x) + Math.abs(e.clientY - pressed.current.y) > 4) pressMoved.current = true
      update()
    }
    const onDown = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      at(e); pressed.current = pos.current; pressMoved.current = false
      update()
    }
    const onUp = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return
      at(e)
      // A press that dragged and ends on the rail must not open it right away.
      if (blockedAfterPress(pressMoved.current, geo.current, pos.current)) hoverBlocked.current = true
      pressed.current = null; pressMoved.current = false
      update()
    }
    const onCancel = () => { pressed.current = null; pressMoved.current = false }
    const onDragStart = () => { dragging.current = true; cancelHover() }
    const onDragOver = (e: DragEvent) => { dragging.current = true; if (e.clientX || e.clientY) at(e); cancelHover() }
    const onDragEnd = (e: DragEvent) => {
      if (e.clientX || e.clientY) at(e)
      dragging.current = false; pressed.current = null; pressMoved.current = false
      if (blockedAfterPress(true, geo.current, pos.current)) hoverBlocked.current = true
      update()
    }
    // Pointer left the window: it is outside everything.
    const onLeaveWindow = () => { pos.current = null; hoverBlocked.current = false; update() }
    // The breakpoint slide (mobile -> desktop) ends with a transitionend on the nav itself.
    const nav = navRef.current
    const onSlideEnd = (e: TransitionEvent) => { if (e.target === nav) measure() }
    nav?.addEventListener('transitionend', onSlideEnd)
    const root = document.documentElement
    const opts = { capture: true, passive: true } as const
    document.addEventListener('pointermove', onMove, opts)
    document.addEventListener('pointerdown', onDown, opts)
    document.addEventListener('pointerup', onUp, opts)
    document.addEventListener('pointercancel', onCancel, opts)
    document.addEventListener('dragstart', onDragStart, opts)
    document.addEventListener('dragenter', onDragStart, opts)
    document.addEventListener('dragover', onDragOver, opts)
    document.addEventListener('dragend', onDragEnd, opts)
    document.addEventListener('drop', onDragEnd, opts)
    root.addEventListener('mouseleave', onLeaveWindow)
    window.addEventListener('resize', measure)
    return () => {
      document.removeEventListener('pointermove', onMove, true)
      document.removeEventListener('pointerdown', onDown, true)
      document.removeEventListener('pointerup', onUp, true)
      document.removeEventListener('pointercancel', onCancel, true)
      document.removeEventListener('dragstart', onDragStart, true)
      document.removeEventListener('dragenter', onDragStart, true)
      document.removeEventListener('dragover', onDragOver, true)
      document.removeEventListener('dragend', onDragEnd, true)
      document.removeEventListener('drop', onDragEnd, true)
      root.removeEventListener('mouseleave', onLeaveWindow)
      window.removeEventListener('resize', measure)
      nav?.removeEventListener('transitionend', onSlideEnd)
    }
  }, [isDesktop, update, cancelHover, measure])
  // The rail icons appear after the permissions load and change with them.
  useLayoutEffect(() => { if (isDesktop) measure() }, [isDesktop, navReady, filteredSections, measure])

  // Start / stop the countdown whenever the overlay opens or closes. The panel is wider than
  // the rail, so "inside" is taken again from the last pointer position.
  useEffect(() => {
    if (!overlayOpen) { cancelIdle(); pointerInside.current = within(geo.current, pos.current, RAIL_W); return }
    pointerInside.current = isDesktop && within(geo.current, pos.current, PANEL_W)
    armIdle()
    return cancelIdle
  }, [overlayOpen, isDesktop, armIdle, cancelIdle])
  useEffect(() => () => { cancelHover(); cancelIdle() }, [cancelHover, cancelIdle])

  // Desktop overlay closes when the viewport shrinks to mobile and vice versa.
  useEffect(() => { if (!isDesktop) setHoverOpen(false) }, [isDesktop])

  // Rail icon / hover on an icon: bring that section into view before paint; by keyboard the
  // focus moves to its first entry.
  useLayoutEffect(() => {
    const r = reveal.current
    if (!r || !expanded) return
    reveal.current = null
    const sc = scrollRef.current
    const el = r.label ? sc?.querySelector<HTMLElement>(`[data-nav-section="${CSS.escape(r.label)}"]`) : null
    if (!sc || !el) return
    el.scrollIntoView({ block: el.offsetHeight > sc.clientHeight ? 'start' : 'nearest' })
    if (r.focus) el.querySelector<HTMLElement>('a[href]')?.focus({ preventScroll: true })
  }, [expanded, openSection])

  // The open panel's nav tree, rebuilt only when what it shows changes — not on the backdrop,
  // hover-timer or drawer state renders (A20).
  const panelTree = useMemo(() => (
    filteredSections.map((section, idx) => {
      const bodyId = sectionBodyId(section.label)
      const isOpen = openSection === section.label
      return (
        <div
          key={section.label}
          data-nav-section={section.label}
          className={`mb-4 lg:mb-0 ${idx > 0 ? 'lg:border-t lg:border-outline-variant/30' : ''}`}
        >
          <SectionLabel
            label={section.label}
            isOpen={isOpen}
            bodyId={bodyId}
            onToggle={() => setOpenSection((prev) => (prev === section.label ? null : section.label))}
          />
          <div
            id={bodyId}
            className={`grid transition-[grid-template-rows,visibility] duration-300 motion-reduce:transition-none ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] invisible'}`}
          >
            <div className="overflow-hidden min-h-0">
              {section.items.map((item, i) => {
                const heading = item.group && item.group !== section.items[i - 1]?.group ? item.group : null
                return (
                  <div key={item.path}>
                    {heading && (
                      <div className="px-3 pt-3 pb-1 text-[10px] font-bold tracking-[0.08em] uppercase text-on-surface-variant">{heading}</div>
                    )}
                    <MainNavItem item={item} isActive={item.path === activePath} />
                    {item.subItems?.map((sub) => (
                      <SubNavItem key={sub.path} item={sub} isActive={sub.path === activePath} />
                    ))}
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      )
    })
  ), [filteredSections, openSection, activePath])

  return (
    <>
      {/* Backdrop: mobile drawer scrim, and on desktop the blurred/dimmed page behind the
          overlay. Purely visual and click-through: the document click listener above closes
          the sidebar and the click still reaches the page element (A16). */}
      {backdropMounted && (
        <div
          aria-hidden="true"
          className={`fixed inset-0 z-[55] bg-slate-950/20 lg:bg-surface/30 backdrop-blur-sm lg:backdrop-blur-[6px] pointer-events-none ${overlayOpen ? 'sidebar-backdrop-in' : 'sidebar-backdrop-out'}`}
        />
      )}

      <nav
        ref={navRef}
        aria-label="Hauptmenü"
        onClick={onNavClick}
        onAnimationEnd={(e) => { if (e.target === e.currentTarget && e.animationName === 'sidebar-conceal') setConcealing(false) }}
        onPointerDownCapture={(e) => { if (e.pointerType === 'touch') armIdle() }}
        onFocusCapture={(e) => {
          // Only keyboard focus counts as "in use"; a mouse click leaves focus on the link.
          let visible = false
          try { visible = (e.target as HTMLElement).matches(':focus-visible') } catch { /* old browser */ }
          focusInside.current = visible
          if (visible) cancelIdle()
        }}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) { focusInside.current = false; armIdle() }
        }}
        className={`liquid-glass rounded-3xl flex flex-col fixed top-4 bottom-4 left-4 z-[60] w-64 transition-[translate,opacity,visibility,background-color] duration-300 motion-reduce:transition-none ease-[cubic-bezier(0.2,0.8,0.2,1)] group/sidebar
          ${iconMode && !concealing ? 'lg:w-[72px]' : 'lg:w-64'} ${overlayOpen ? 'sidebar-panel-open' : ''}
          ${isDesktop && hoverOpen ? 'sidebar-reveal' : isDesktop && concealing ? 'sidebar-conceal' : ''}
          ${isMobileOpen
            ? 'translate-x-0 opacity-100 visible pointer-events-auto'
            : '-translate-x-full lg:translate-x-0 opacity-0 lg:opacity-100 invisible lg:visible pointer-events-none lg:pointer-events-auto'}`}
      >
        {/* Floating toggle (desktop only): opens / closes the overlay. */}
        <button
          ref={toggleRef}
          type="button"
          onClick={toggle}
          aria-label={hoverOpen ? 'Menü schließen' : 'Menü öffnen'}
          aria-expanded={hoverOpen}
          title={hoverOpen ? 'Menü schließen' : 'Menü öffnen'}
          className={`hidden lg:flex absolute -right-3 top-8 w-6 h-6 bg-surface-container-lowest border border-outline-variant rounded-full items-center justify-center shadow-sm z-[60] opacity-0 group-hover/sidebar:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100 transition-all duration-200 motion-reduce:transition-none hover:scale-110 hover:border-primary text-outline-variant hover:text-primary outline-none ${FOCUS_RING}`}
        >
          <span
            aria-hidden="true"
            className={`material-symbols-outlined !text-[16px] transition-transform duration-300 motion-reduce:transition-none ${hoverOpen ? 'rotate-0' : 'rotate-180'}`}
          >
            chevron_left
          </span>
        </button>

        {/* Header / Logo — theme-aware brand block (brand const resolved in component body) */}
        {/* Same 20 px inset in the rail (centred in 72 px) and in the panel: nothing in the header
            moves or re-lays out while the panel slides (A20). */}
        <div className="flex items-center h-16 lg:h-12 flex-shrink-0 overflow-hidden px-5 gap-3 border-b border-outline-variant/30">
          <div className="relative w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
            <span aria-hidden="true" className="text-on-primary font-bold text-xs font-headline">{brand.monogram}</span>
            {brand.stripe && (
              <span
                aria-hidden="true"
                className="absolute bottom-0 left-0 right-0 h-[3px]"
                style={{ background: 'linear-gradient(90deg, #2B1E19 0%, #E30613 50%, #2B1E19 100%)' }}
              />
            )}
          </div>
          <div className={`min-w-0 ${iconMode ? 'sr-only' : ''}`}>
            <p className="font-headline font-bold text-on-surface text-[15px] leading-tight truncate">
              {brand.name}
            </p>
            <p className="text-[10px] text-outline-variant font-medium tracking-widest uppercase mt-0.5">
              {brand.tagline}
            </p>
          </div>

          {/* Mobile Close Button */}
          <button
            type="button"
            onClick={() => {
              // Focus would drop to <body> inside the now-inert nav (same as the Escape path).
              document.querySelector<HTMLElement>('header button[aria-label="Menü öffnen"]')?.focus()
              setIsMobileOpen(false)
            }}
            aria-label="Menü schließen"
            className={`lg:hidden ml-auto p-1.5 rounded-md text-outline-variant hover:text-on-surface outline-none ${FOCUS_RING}`}
          >
            <span aria-hidden="true" className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Nav area. Desktop: the section rail (A22) is ALWAYS there, at the same x/y, closed and
            open; the open panel is that rail column plus the section accordion to its right. A
            pointer on a rail icon therefore never lands on a menu link by a hover-open (R19). */}
        {isDesktop && navReady ? (
          <div className="flex-1 min-h-0 flex gap-2 px-3 py-2">
            {/* Fixed 48 px column: the icons stay in place while the panel opens and closes. */}
            <div className="flex flex-col items-center gap-1 w-12 flex-shrink-0 overflow-y-auto overflow-x-hidden no-scrollbar" onScroll={measure}>
              {filteredSections.map((section) => {
                const active = section.label === activeSection
                const selected = expanded && section.label === openSection
                return (
                  <button
                    key={section.label}
                    type="button"
                    data-rail-section={section.label}
                    // Hover or click opens THAT section; in the open panel a click only selects it.
                    onClick={(e) => { cancelHover(); openOverlayAt(section.label, e.detail === 0) }}
                    aria-label={section.label}
                    title={section.label}
                    aria-current={active ? 'true' : undefined}
                    aria-expanded={selected}
                    aria-controls={expanded ? sectionBodyId(section.label) : undefined}
                    className={`relative flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-[10px] cursor-pointer transition-colors duration-200 motion-reduce:transition-none outline-none ${FOCUS_RING} ${active
                      ? 'bg-primary/10 text-primary'
                      : selected
                        ? 'bg-surface-container text-on-surface'
                        : 'text-outline-variant hover:bg-surface-container hover:text-on-surface'}`}
                  >
                    {active && <span aria-hidden="true" className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full" />}
                    <span aria-hidden="true" className="material-symbols-outlined !text-[22px]">{section.icon}</span>
                  </button>
                )
              })}
            </div>
            {expanded && (
              <div ref={scrollRef} className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden no-scrollbar flex flex-col">
                {panelTree}
              </div>
            )}
          </div>
        ) : (
          <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto px-3 py-4 lg:py-2 overflow-x-hidden no-scrollbar flex flex-col">
            {permsLoading ? (
              <NavSkeleton />
            ) : !navReady ? (
              <div className="px-3 pt-4 flex flex-col gap-2 text-[12px] text-on-surface-variant" role="alert">
                {!iconMode && <p>Menü konnte nicht geladen werden.</p>}
                <button
                  type="button"
                  onClick={() => { void refetchPerms() }}
                  aria-label="Menü erneut laden"
                  className={`self-start inline-flex items-center gap-1.5 rounded-md px-2 py-1 font-semibold text-primary hover:bg-primary/10 outline-none ${FOCUS_RING}`}
                >
                  <span aria-hidden="true" className="material-symbols-outlined !text-[16px]">refresh</span>
                  {!iconMode && 'Erneut versuchen'}
                </button>
              </div>
            ) : (
              panelTree
            )}
          </div>
        )}

      </nav>
    </>
  )
}
